import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSlashStore } from "../store/slashStore";
import { useAIContext } from "../store/aiContextStore";
import LoadingSpinner from "../components/common/LoadingSpinner";
import ErrorBanner from "../components/common/ErrorBanner";

/**
 * Compliance Slasher Enforcement Logic (v2)
 * 
 * UI for managing compliance gap scoring, enforcement penalties,
 * passport downgrades, and remediation tracking.
 */
export default function ComplianceSlasherV2() {
  const { t } = useTranslation();
  const {
    gapScore,
    severity,
    crossBorderFactor,
    supplyChainMultiplier,
    subsidyEligible,
    remediationDays,
    remediationCostEUR,
    setGapScore,
    setSeverity,
    setCrossBorderFactor,
    setSupplyChainMultiplier,
    setSubsidyEligible,
    setRemediationDays,
    setRemediationCostEUR,
    exportEnforcementReport,
  } = useSlashStore();

  // Set active panel for AI context
  useEffect(() => {
    useAIContext.getState().setActivePanel("Compliance Slasher");
  }, []);

  // Update AI context inputs on change
  useEffect(() => {
    useAIContext.getState().setInput("gapScore", gapScore);
    useAIContext.getState().setInput("severity", severity);
    useAIContext.getState().setInput("crossBorderFactor", crossBorderFactor);
    useAIContext.getState().setInput("supplyChainMultiplier", supplyChainMultiplier);
  }, [gapScore, severity, crossBorderFactor, supplyChainMultiplier]);

  const enforcementPenalty = useSlashStore((s) => s.getEnforcementPenalty());
  const passportDowngrade = useSlashStore((s) => s.getPassportDowngrade());
  const co2TaxAdjustment = useSlashStore((s) => s.getCo2TaxAdjustment());
  const frictionIndex = useSlashStore((s) => s.getFrictionIndex());

  // Loading and error states
  const [error, setError] = useState<Error | null>(null);

  const handleExport = () => {
    setError(null);
    try {
      exportEnforcementReport();
    } catch (e) {
      const error = e instanceof Error ? e : new Error(t("slasher.exportFailed") || "Failed to export enforcement report");
      setError(error);
      console.error("Export failed:", e);
    }
  };

  return (
    <div className="space-y-6">
      <div className="font-orbitron text-evat-neon text-xl mb-4">
        {t("slasher.title") || "Compliance Slasher Enforcement"}
      </div>

      <ErrorBanner error={error} onRetry={() => setError(null)} showRetry={!!error} />

      {/* Input Controls */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="text-sm text-neutral-300">
            {t("slasher.gapScore") || "Gap Score (0-100)"}
          </label>
          <input
            type="number"
            min="0"
            max="100"
            value={gapScore}
            onChange={(e) => setGapScore(Number(e.target.value))}
            className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2 focus:outline-none focus:border-evat-gold"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm text-neutral-300">
            {t("slasher.severity") || "Severity"}
          </label>
          <select
            value={severity}
            onChange={(e) => setSeverity(e.target.value as any)}
            className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2 focus:outline-none focus:border-evat-gold"
          >
            <option value="low">{t("slasher.severityLow") || "Low"}</option>
            <option value="medium">{t("slasher.severityMedium") || "Medium"}</option>
            <option value="high">{t("slasher.severityHigh") || "High"}</option>
          </select>
        </div>

        <div className="space-y-2">
          <label className="text-sm text-neutral-300">
            {t("slasher.crossBorderFactor") || "Cross-Border Factor"}
          </label>
          <input
            type="number"
            step="0.1"
            min="0"
            value={crossBorderFactor}
            onChange={(e) => setCrossBorderFactor(Number(e.target.value))}
            className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2 focus:outline-none focus:border-evat-gold"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm text-neutral-300">
            {t("slasher.supplyChainMultiplier") || "Supply Chain Multiplier"}
          </label>
          <input
            type="number"
            step="0.1"
            min="0"
            value={supplyChainMultiplier}
            onChange={(e) => setSupplyChainMultiplier(Number(e.target.value))}
            className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2 focus:outline-none focus:border-evat-gold"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm text-neutral-300">
            {t("slasher.remediationDays") || "Remediation Days"}
          </label>
          <input
            type="number"
            min="0"
            value={remediationDays}
            onChange={(e) => setRemediationDays(Number(e.target.value))}
            className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2 focus:outline-none focus:border-evat-gold"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm text-neutral-300">
            {t("slasher.remediationCost") || "Remediation Cost (EUR)"}
          </label>
          <input
            type="number"
            min="0"
            value={remediationCostEUR}
            onChange={(e) => setRemediationCostEUR(Number(e.target.value))}
            className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2 focus:outline-none focus:border-evat-gold"
          />
        </div>

        <div className="space-y-2 flex items-center">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={subsidyEligible}
              onChange={(e) => setSubsidyEligible(e.target.checked)}
              className="w-4 h-4 bg-black border border-evat-gold text-evat-neon rounded focus:ring-evat-gold"
            />
            <span className="text-sm text-neutral-300">
              {t("slasher.subsidyEligible") || "Subsidy Eligible"}
            </span>
          </label>
        </div>
      </div>

      {/* Derived Metrics Display */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
        <div className="border border-evat-gold/30 rounded p-3 bg-black/50">
          <div className="text-xs text-neutral-400 mb-1">
            {t("slasher.enforcementPenalty") || "Penalty"}
          </div>
          <div className="text-lg font-orbitron text-red-400">
            {enforcementPenalty.toFixed(2)}
          </div>
        </div>

        <div className="border border-evat-gold/30 rounded p-3 bg-black/50">
          <div className="text-xs text-neutral-400 mb-1">
            {t("slasher.passportDowngrade") || "Passport ↓"}
          </div>
          <div className="text-lg font-orbitron text-yellow-400">
            -{passportDowngrade}
          </div>
        </div>

        <div className="border border-evat-gold/30 rounded p-3 bg-black/50">
          <div className="text-xs text-neutral-400 mb-1">
            {t("slasher.co2TaxAdjustment") || "CO₂ Tax Adj."}
          </div>
          <div className="text-lg font-orbitron text-orange-400">
            {co2TaxAdjustment.toFixed(2)}
          </div>
        </div>

        <div className="border border-evat-gold/30 rounded p-3 bg-black/50">
          <div className="text-xs text-neutral-400 mb-1">
            {t("slasher.frictionIndex") || "Friction Index"}
          </div>
          <div className="text-lg font-orbitron text-purple-400">
            {frictionIndex.toFixed(3)}
          </div>
        </div>
      </div>

      {/* Export Button */}
      <div className="fixed bottom-4 right-4">
        <button
          onClick={handleExport}
          className="px-4 py-2 bg-evat-gold/20 border border-evat-gold text-evat-gold rounded hover:bg-evat-gold/30 transition-colors font-orbitron disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label={t("slasher.export") || "Export JSON report"}
        >
          {t("slasher.export") || "Export JSON"}
        </button>
      </div>
    </div>
  );
}






